/**
 * `@flozic/shared/api` — the HTTP client + typed resources.
 *
 * `createApiClient(opts)` builds an {@link HttpClient} and attaches every typed
 * resource. The dashboard uses the authenticated resources; the widget uses
 * {@link ApiClient.widget} (the public surface). See ARCHITECTURE §7.
 */

import { HttpClient, type HttpClientOptions } from './httpClient';
import {
  ChatsResource,
  AgentsResource,
  CustomersResource,
  OrganizationsResource,
  ChannelsResource,
  CannedResponsesResource,
  TicketsResource,
  DepartmentsResource,
  ModerationResource,
  AnalyticsResource,
  VoiceResource,
  VisitorsResource,
  AgentChatResource,
  RolesResource,
  TranslateResource,
  ComposeResource,
  OutboundResource,
  PushResource,
  BillingResource,
  WhatsAppRelayResource,
  WidgetPublicResource,
} from './resources';

export * from './httpClient';
export * from './endpoints';
export * from './resources';

/** All resources bound to a single {@link HttpClient}. */
export interface ApiClient {
  http: HttpClient;
  chats: ChatsResource;
  agents: AgentsResource;
  customers: CustomersResource;
  organizations: OrganizationsResource;
  channels: ChannelsResource;
  cannedResponses: CannedResponsesResource;
  tickets: TicketsResource;
  departments: DepartmentsResource;
  moderation: ModerationResource;
  analytics: AnalyticsResource;
  voice: VoiceResource;
  /** Live visitors currently browsing with the widget loaded. */
  visitors: VisitorsResource;
  /** Staff-to-staff DMs (Online Agents panel). */
  agentChat: AgentChatResource;
  /** Custom roles + permission catalog. */
  roles: RolesResource;
  /** Chat-translation proxy. */
  translate: TranslateResource;
  /** Smart Compose (inline composer completion). */
  compose: ComposeResource;
  /** Agent dialer — outbound WhatsApp/SMS + capability probe. */
  outbound: OutboundResource;
  /** Web Push subscriptions (agent desktop notifications via service worker). */
  push: PushResource;
  billing: BillingResource;
  whatsappRelay: WhatsAppRelayResource;
  /** Public widget surface (anonymous / session-token scoped). */
  widget: WidgetPublicResource;
}

/** Construct a fully-wired API client for either app. */
export function createApiClient(opts: HttpClientOptions): ApiClient {
  const http = new HttpClient(opts);
  return {
    http,
    chats: new ChatsResource(http),
    agents: new AgentsResource(http),
    customers: new CustomersResource(http),
    organizations: new OrganizationsResource(http),
    channels: new ChannelsResource(http),
    cannedResponses: new CannedResponsesResource(http),
    tickets: new TicketsResource(http),
    departments: new DepartmentsResource(http),
    moderation: new ModerationResource(http),
    analytics: new AnalyticsResource(http),
    voice: new VoiceResource(http),
    visitors: new VisitorsResource(http),
    agentChat: new AgentChatResource(http),
    roles: new RolesResource(http),
    translate: new TranslateResource(http),
    compose: new ComposeResource(http),
    outbound: new OutboundResource(http),
    push: new PushResource(http),
    billing: new BillingResource(http),
    whatsappRelay: new WhatsAppRelayResource(http),
    widget: new WidgetPublicResource(http),
  };
}
